import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Megaphone, Lock, CheckCircle2, X, ArrowRight, Mail, Phone as PhoneIcon, Globe, MapPin, Star, Shield, Tv, Users, Briefcase, Coffee, Trophy, Sparkles, Shirt, Volume2, Zap, Eye, Send, Building2, FileText, ChevronRight, Calendar, Play, Image as ImageIcon, ExternalLink } from "lucide-react";
import BusinessPricingTabs from "../../components/sport/BusinessPricingTabs";
import { TornEdge } from "../../components/sport/TornEdge";
import { api } from "../../lib/api";
import { useCustomerAuth } from "../../lib/useCustomerAuth";
import { MALE_SILHOUETTE } from "../../lib/silhouettes";

const PRIMARY = "#4F1B7C";
const SECONDARY = "#EC1F7C";
const BG_DARK = "#160732";
const BG_DEEP = "#280F4F";
const AVAILABLE_GREEN = "#10B981";
const RESERVED_RED = "#EF4444";

const TIER_INFO = {
  institutional: { label: "Partenaire Institutionnel", color: "#cd7f32", bg: "linear-gradient(135deg, #d49159, #8a4f1c)" },
  major: { label: "Partenaire Majeur", color: "#FFD700", bg: "linear-gradient(135deg, #ffd700, #ff9d00)" },
  premium: { label: "Partenaire Premium", color: "#FFD700", bg: "linear-gradient(135deg, #ffd700, #ff9d00)" },
  equipementier: { label: "Équipementier", color: "#22c55e", bg: "linear-gradient(135deg, #22c55e, #16a34a)" },
  supplier: { label: "Fournisseur Officiel", color: "#a78bfa", bg: "linear-gradient(135deg, #a78bfa, #7c3aed)" },
  official: { label: "Partenaire Officiel", color: "#C0C0C0", bg: "linear-gradient(135deg, #cfcfcf, #909090)" },
  branding: { label: "Visibilité branding", color: "#a78bfa", bg: `linear-gradient(135deg, #a78bfa, ${PRIMARY})` },
};

// Zone media (photos + videos) — would be CMS-driven in production
const BROCHURE_URL = "https://customer-assets.emergentagent.com/job_pod-istres-hb/artifacts/6baiabou_Istres%20Provence%20Handball%20-%2025_26.pdf";
const PRESENTATION_VIDEO = "https://www.youtube.com/embed/videoseries?list=UU1_vd1xaPipfBjhqJBaXCSg";

// ============================================================
// Sidebar (sticky) — annuaire CTA, quick actions, plaquette download
// ============================================================
function PartnersSidebar({ partnerCount, onLogin, customer, onScrollContact }) {
  const goSection = (id) => () => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  return (
    <aside data-testid="partners-sidebar" className="lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto space-y-4 pr-1 sidebar-scroll">
      {/* PRIMARY CTA — Devenir partenaire (highlighted) */}
      <button
        onClick={onScrollContact}
        data-testid="sidebar-cta-devenir-partenaire"
        className="w-full relative rounded-3xl overflow-hidden shadow-2xl group hover:scale-[1.02] active:scale-[0.99] transition"
        style={{ background: `linear-gradient(135deg, ${SECONDARY} 0%, #ff3a8c 50%, #c9176d 100%)` }}
      >
        <span className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/15 blur-xl"/>
        <span className="absolute inset-0 opacity-30 mix-blend-overlay" style={{
          backgroundImage: "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2280%22 height=%2280%22><circle cx=%221%22 cy=%221%22 r=%221%22 fill=%22%23ffffff%22/></svg>')",
        }}/>
        <div className="relative p-5 text-left text-white">
          <div className="flex items-center gap-2 text-white/90 font-black tracking-[0.3em] text-[10px] uppercase mb-1">
            <Megaphone size={11}/> Rejoindre l'aventure
          </div>
          <div className="font-blackhawk italic uppercase text-2xl sm:text-3xl leading-none">
            Devenir<br/>partenaire
          </div>
          <p className="text-white/85 text-[11px] mt-2 leading-snug">Demande de rappel sous 48h ouvrées</p>
          <span className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest bg-white/15 group-hover:bg-white/25 backdrop-blur px-3 py-1.5 rounded-full transition">
            Je veux être contacté <ArrowRight size={12} className="group-hover:translate-x-1 transition"/>
          </span>
        </div>
      </button>

      {/* SECONDARY CTA — Découvrir les offres */}
      <button
        onClick={goSection("prestations")}
        data-testid="sidebar-cta-decouvrir-offres"
        className="w-full rounded-3xl bg-white shadow-xl border-2 border-pink-200 hover:border-pink-400 hover:shadow-2xl transition group"
      >
        <div className="p-4 flex items-center gap-3 text-left">
          <span className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-lg shrink-0" style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${BG_DEEP})` }}>
            <Trophy size={18}/>
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-black uppercase text-sm leading-tight" style={{ color: PRIMARY }}>Découvrir les offres</div>
            <div className="text-[11px] text-slate-500 mt-0.5">Hospitalité · Visibilité · Médias</div>
          </div>
          <ArrowRight size={14} className="text-pink-500 group-hover:translate-x-1 transition shrink-0"/>
        </div>
      </button>

      {/* QUICK NAV */}
      <div className="rounded-3xl bg-white shadow-xl border border-slate-100 overflow-hidden">
        <div className="px-5 py-3 text-pink-600 font-black tracking-widest text-[10px] uppercase border-b border-slate-100">Navigation rapide</div>
        <div className="p-2">
          {[
            { id: "presentation", label: "Présentation vidéo", icon: Play },
            { id: "events", label: "Événements Business", icon: Calendar },
            { id: "zones", label: "Plan terrain interactif", icon: MapPin },
            { id: "annuaire", label: `Annuaire (${partnerCount})`, icon: Building2 },
            { id: "contact", label: "Formulaire de contact", icon: Send },
          ].map((it) => (
            <button key={it.id} onClick={goSection(it.id)} data-testid={`sidebar-nav-${it.id}`}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-pink-50 transition text-left group">
              <span className="w-7 h-7 rounded-lg bg-pink-50 group-hover:bg-pink-500/20 text-pink-600 flex items-center justify-center transition shrink-0">
                <it.icon size={13}/>
              </span>
              <span className="font-bold text-sm text-slate-700 group-hover:text-pink-700 transition flex-1">{it.label}</span>
              <ChevronRight size={13} className="text-slate-300 group-hover:text-pink-500 transition"/>
            </button>
          ))}
        </div>
      </div>

      {/* PLAQUETTE DOWNLOAD */}
      <a href={BROCHURE_URL} target="_blank" rel="noopener noreferrer" data-testid="sidebar-brochure"
        className="block rounded-3xl overflow-hidden shadow-xl group hover:scale-[1.01] transition"
        style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${BG_DEEP})` }}>
        <div className="relative p-5 text-white">
          <div className="absolute top-3 right-3 w-12 h-12 rounded-full bg-white/15 backdrop-blur flex items-center justify-center">
            <FileText size={22}/>
          </div>
          <div className="text-pink-300 font-black tracking-widest text-[10px] uppercase">Téléchargez</div>
          <h3 className="font-black uppercase italic text-xl leading-tight mt-1">Plaquette<br/>commerciale 25/26</h3>
          <div className="mt-3 inline-flex items-center gap-2 text-[11px] font-black tracking-widest uppercase opacity-90 group-hover:opacity-100">
            PDF officiel <ArrowRight size={12}/>
          </div>
        </div>
      </a>

      {/* COMMERCIAL CONTACT — Lilian Di Salvo (real contact) */}
      <div className="rounded-3xl bg-white shadow-xl border border-slate-100 overflow-hidden" data-testid="commercial-contacts">
        <div className="px-5 py-3 text-pink-600 font-black tracking-widest text-[10px] uppercase border-b border-slate-100 flex items-center gap-2">
          <Briefcase size={12}/> Votre contact business
        </div>
        <div className="p-4">
          <div className="flex gap-3 p-3 rounded-2xl bg-gradient-to-br from-pink-50 to-purple-50 items-center">
            {/* Round photo (silhouette fallback if no photo provided) */}
            <div className="w-16 h-16 rounded-full overflow-hidden shrink-0 shadow-md ring-4 ring-white"
                 style={{ background: `linear-gradient(135deg, ${SECONDARY}, ${PRIMARY})` }}>
              <img
                src="/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2024%2F01%2FDSC02492-PhotoRoom.png"
                alt="Lilian Di Salvo"
                className="w-full h-full object-cover object-[center_top]"
                loading="lazy"
                onError={(e) => { e.currentTarget.src = MALE_SILHOUETTE; }}
                data-testid="commercial-photo"
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-black text-sm leading-tight" style={{ color: PRIMARY }}>Lilian Di Salvo</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mt-0.5">Resp. dév. commercial</div>
              <div className="mt-2 space-y-1">
                <a href="mailto:partenariat@istreshandball.com" className="flex items-center gap-1.5 text-[11px] text-slate-700 hover:text-pink-600 truncate" data-testid="commercial-email">
                  <Mail size={11} className="shrink-0 text-pink-500"/> <span className="truncate">partenariat@istreshandball.com</span>
                </a>
                <a href="tel:0675920670" className="flex items-center gap-1.5 text-[11px] font-black text-slate-800 hover:text-pink-600" data-testid="commercial-phone">
                  <PhoneIcon size={11} className="shrink-0 text-pink-500"/> 06 75 92 06 70
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* DIRECTORY ACCESS */}
      <div className="rounded-3xl bg-[#160732] shadow-xl overflow-hidden">
        <div className="px-5 py-3 text-pink-300 font-black tracking-widest text-[10px] uppercase border-b border-white/10 flex items-center gap-2">
          <Building2 size={12}/> Annuaire ({partnerCount})
        </div>
        <div className="p-5 text-white">
          <p className="text-white/70 text-sm mb-4">L'écosystème complet des partenaires du club.</p>
          {customer ? (
            <a href="#annuaire" data-testid="sidebar-go-directory"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full font-black uppercase text-[11px] tracking-widest hover:scale-105 transition"
              style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, color: "white" }}>
              Voir l'annuaire <ArrowRight size={12}/>
            </a>
          ) : (
            <button onClick={onLogin} data-testid="sidebar-login"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full font-black uppercase text-[11px] tracking-widest hover:scale-105 transition"
              style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, color: "white" }}>
              <Lock size={12}/> Voir les contacts
            </button>
          )}
          <p className="text-white/40 text-[10px] mt-3 italic">Coordonnées (email & téléphone) accessibles aux partenaires connectés uniquement.</p>
        </div>
      </div>
    </aside>
  );
}

// ============================================================
// BUSINESS EVENTS GALLERY — fed by /api/sport/news?category=partenaires
// ============================================================
function BusinessEventsGallery() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // selected event for lightbox

  useEffect(() => {
    api.get("/sport/news?category=partenaires&limit=24")
      .then((r) => setEvents(r.data?.items || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  if (!loading && events.length === 0) return null;

  return (
    <section id="events" className="rounded-3xl bg-white shadow-xl border border-slate-100 overflow-hidden" data-testid="business-events-gallery">
      <div className="px-6 py-5 border-b border-slate-100 flex items-end justify-between gap-4">
        <div>
          <div className="text-pink-600 font-black tracking-[0.3em] text-[10px] uppercase">Galerie</div>
          <h3 className="font-black uppercase italic text-xl sm:text-2xl mt-0.5" style={{ color: PRIMARY }}>
            Événements <span style={{ color: SECONDARY }}>Business Club</span>
          </h3>
          <p className="text-slate-500 text-sm mt-1.5 max-w-xl">Dîners, rencontres, soirées privatisées, immersions partenaires : revivez les temps forts du Business Club.</p>
        </div>
        <Link to="/sport/actualites?cat=partenaires"
           className="hidden sm:inline-flex items-center gap-1.5 text-pink-600 font-black text-[11px] uppercase tracking-widest hover:text-pink-800 transition" data-testid="business-events-all">
          Tout voir <ArrowRight size={12}/>
        </Link>
      </div>

      <div className="p-4 sm:p-6">
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3,4,5,6].map((i) => <div key={i} className="aspect-[4/3] rounded-2xl bg-slate-100 animate-pulse"/>)}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {events.slice(0, 9).map((e) => (
              <button
                key={e.id}
                onClick={() => setActive(e)}
                className="group relative aspect-[4/3] rounded-2xl overflow-hidden bg-slate-200 shadow hover:shadow-2xl transition border border-slate-100 hover:border-pink-300 text-left"
                data-testid={`business-event-${e.id}`}
              >
                {e.image ? (
                  <img src={e.image} alt={e.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"/>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-pink-300/60" style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${BG_DEEP})` }}>
                    <ImageIcon size={40}/>
                  </div>
                )}
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 40%, rgba(22,7,50,0.95) 100%)" }}/>
                <div className="absolute inset-x-0 bottom-0 p-3 text-white">
                  <div className="text-pink-300 text-[10px] font-black uppercase tracking-widest mb-1">
                    {e.date_iso ? new Date(e.date_iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : ""}
                  </div>
                  <div className="font-black text-sm leading-tight line-clamp-2" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.6)" }}>{e.title}</div>
                </div>
                <span className="absolute top-2 right-2 w-9 h-9 rounded-full bg-white/15 backdrop-blur opacity-0 group-hover:opacity-100 flex items-center justify-center transition">
                  <Eye size={15} className="text-white"/>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setActive(null)}
            data-testid="business-event-lightbox"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-4xl rounded-3xl bg-white overflow-hidden shadow-2xl"
            >
              <button onClick={() => setActive(null)} className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-black/60 hover:bg-pink-600 text-white flex items-center justify-center transition" aria-label="Fermer">
                <X size={18}/>
              </button>
              <div className="aspect-[16/9] bg-black">
                {active.image && <img src={active.image} alt={active.title} className="w-full h-full object-cover"/>}
              </div>
              <div className="p-6">
                <div className="text-pink-600 font-black tracking-[0.3em] text-[10px] uppercase mb-2">
                  {active.date_iso ? new Date(active.date_iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : ""}
                </div>
                <h3 className="font-black uppercase italic text-2xl sm:text-3xl leading-tight" style={{ color: PRIMARY }}>{active.title}</h3>
                {active.excerpt && <p className="text-slate-600 mt-3 text-sm leading-relaxed">{active.excerpt}</p>}
                {active.url && (
                  <Link to={`/sport/actualites/${active.slug}`}
                     onClick={() => setActive(null)}
                     className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full font-black uppercase text-xs tracking-widest text-white shadow-lg hover:scale-105 transition"
                     style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}
                     data-testid="business-event-read-more">
                    Lire l'article complet <ArrowRight size={14}/>
                  </Link>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

// ============================================================
// CLUB PRESENTATION VIDEO BLOCK
// ============================================================
function ClubPresentation() {
  return (
    <div data-testid="club-presentation" className="rounded-3xl overflow-hidden shadow-2xl"
      style={{ background: `linear-gradient(135deg, ${BG_DEEP}, ${PRIMARY})` }}>
      <div className="grid md:grid-cols-[1fr,0.9fr] gap-0">
        <div className="relative aspect-video md:aspect-auto md:min-h-[280px] bg-black">
          <iframe title="Présentation IPHB"
            src={PRESENTATION_VIDEO}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen className="absolute inset-0 w-full h-full"/>
        </div>
        <div className="p-6 sm:p-8 text-white relative">
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `radial-gradient(circle, ${SECONDARY} 1px, transparent 1px)`,
            backgroundSize: "12px 12px",
          }}/>
          <div className="relative">
            <div className="text-pink-300 font-black tracking-[0.3em] text-[10px] uppercase mb-2">Présentation</div>
            <h3 className="font-black uppercase italic text-2xl sm:text-3xl leading-tight">
              Istres, terre de <span style={{ color: SECONDARY }}>handball</span>
            </h3>
            <p className="text-white/85 text-sm mt-3 leading-relaxed">
              Plongez dans l'univers de l'IPHB : un club historique en Liqui Moly StarLigue, un projet ambitieux et une communauté
              passionnée. Découvrez en quelques minutes ce qui rend l'aventure unique.
            </p>
            <ul className="mt-4 space-y-2 text-sm">
              {["Élite nationale — StarLigue", "Médias TV : BeIN Sports & Handball TV", "+100 entreprises partenaires", "Projet engagé sur le territoire"].map((it) => (
                <li key={it} className="flex items-center gap-2 text-white/90">
                  <CheckCircle2 size={14} className="text-emerald-400 shrink-0"/> {it}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// PARTNERS SHOWCASE (logo grid)
// ============================================================
function PartnersShowcase({ partners }) {
  if (!partners || partners.length === 0) return null;
  // Pick top tier first then official
  const tierOrder = { institutional: 0, major: 1, premium: 1, equipementier: 2, supplier: 3, official: 4, branding: 5 };
  const sorted = [...partners].sort((a, b) => (tierOrder[a.tier] ?? 9) - (tierOrder[b.tier] ?? 9));
  const featured = sorted.slice(0, 12);

  return (
    <div data-testid="partners-showcase" className="rounded-3xl bg-white shadow-xl border border-slate-100 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <div className="text-pink-600 font-black tracking-[0.3em] text-[10px] uppercase">Ils nous font confiance</div>
          <h3 className="font-black uppercase italic text-xl sm:text-2xl mt-0.5" style={{ color: PRIMARY }}>
            Nos <span style={{ color: SECONDARY }}>partenaires</span>
          </h3>
        </div>
        <a href="#annuaire" className="hidden sm:inline-flex items-center gap-1 text-[11px] font-black tracking-widest uppercase text-pink-600 hover:text-pink-700">
          Annuaire complet <ArrowRight size={11}/>
        </a>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3 p-4 sm:p-6">
        {featured.map((p, i) => (
          <motion.a key={p.id || i}
            href={p.website_url || "#"} target="_blank" rel="noopener noreferrer"
            initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: i * 0.03 }}
            className="group relative aspect-square rounded-2xl bg-slate-50 hover:bg-white hover:shadow-lg border border-slate-100 hover:border-pink-200 transition flex items-center justify-center p-3"
            data-testid={`showcase-partner-${i}`}
            title={p.name}>
            {p.logo_url ? (
              <img src={p.logo_url} alt={p.name} className="max-w-full max-h-full object-contain grayscale group-hover:grayscale-0 transition"/>
            ) : (
              <span className="text-xs font-bold text-slate-500 text-center">{p.name}</span>
            )}
            {p.tier === "premium" && (
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center shadow"
                style={{ background: TIER_INFO.premium.bg }} title="Partenaire Premium">
                <Star size={10} fill="white" className="text-white"/>
              </span>
            )}
          </motion.a>
        ))}
      </div>
    </div>
  );
}

const TIER_INFO_KEYS_SET = ["premium", "official", "institutional", "branding"];

const ZONE_MEDIA = {
  rond_central: {
    photos: [
      "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F04%2F5F6A0557-scaled-e1776434253613.jpg",
      "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F02%2F0J5A1912-scaled-e1770806108734.jpg",
    ],
    video: "https://www.youtube.com/embed/dQw4w9WgXcQ",
    benefits: ["Visibilité TV optimale", "Vue caméra principale", "Coup d'envoi sponsorisé"],
  },
  led_panneautique: {
    photos: [
      "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F03%2F0J5A7467-scaled-e1773324091159.jpg",
      "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F04%2F5F6A0557-scaled-e1776434253613.jpg",
    ],
    benefits: ["80, 40 ou 20 mètres au choix", "Animations dynamiques", "Passages 15 secondes"],
  },
  sticker_zone_g: {
    photos: ["/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F02%2F0J5A1912-scaled-e1770806108734.jpg"],
    benefits: ["+40 min d'exposition TV", "Format 450 × 130 cm", "Vue caméra de chaque action"],
  },
  sticker_zone_d: {
    photos: ["/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F02%2F0J5A1912-scaled-e1770806108734.jpg"],
    benefits: ["+40 min d'exposition TV", "Format 450 × 130 cm", "Vue caméra de chaque action"],
  },
  sticker_but_g: {
    photos: ["/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F04%2F0J5A3051-scaled-e1775208468422.jpg"],
    benefits: ["Devant le but, ultra visible", "Captée à chaque tir", "Format 300 × 100 cm"],
  },
  sticker_but_d: {
    photos: ["/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F04%2F0J5A3051-scaled-e1775208468422.jpg"],
    benefits: ["Devant le but, ultra visible", "Captée à chaque tir", "Format 300 × 100 cm"],
  },
  sticker_arriere_but_g: {
    photos: ["/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F02%2F5F6A6737-scaled-e1771509757955.jpg"],
    benefits: ["Captée par toutes les caméras", "Format 450 × 100 cm", "Visibilité continue"],
  },
  sticker_arriere_but_d: {
    photos: ["/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F02%2F5F6A6737-scaled-e1771509757955.jpg"],
    benefits: ["Captée par toutes les caméras", "Format 450 × 100 cm", "Visibilité continue"],
  },
  ligne_touche_haut: {
    photos: ["/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F04%2F5F6A0557-scaled-e1776434253613.jpg"],
    benefits: ["Visible des tribunes principales", "Longueur libre × 100 cm", "Branding latéral"],
  },
  table_marque: {
    photos: ["/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F03%2F0J5A0165-scaled.jpg"],
    benefits: ["Vue caméra principale", "Présent à chaque ralenti", "Branding officiel"],
  },
  banc_touche_g: {
    photos: ["/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F04%2F0J5A3051-scaled-e1775208468422.jpg"],
    benefits: ["Champ caméra coachs", "Visible joueurs sortis", "Bandeau au-dessus du banc"],
  },
  banc_touche_d: {
    photos: ["/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F04%2F0J5A3051-scaled-e1775208468422.jpg"],
    benefits: ["Champ caméra coachs", "Visible joueurs sortis", "Bandeau au-dessus du banc"],
  },
};

// ============================================================
// Interactive Court SVG (LNH ad placements)
// ============================================================
function CourtPlan({ zones, onSelect, selected }) {
  // Court is rendered on a 100x60 viewBox for easier % positioning
  const W = 100, H = 60;
  const LINE = "#1a1a2e"; // dark navy court lines on white
  const FLOOR = "#ffffff";

  return (
    <div className="relative w-full overflow-hidden rounded-3xl shadow-2xl border border-slate-200" style={{ background: FLOOR }}>
      {/* subtle gradient sheen for depth (kept very light) */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: `radial-gradient(ellipse at 50% 0%, rgba(107,45,92,0.04), transparent 60%)`,
      }}/>

      <svg viewBox={`0 0 ${W} ${H}`} className="block w-full h-auto" preserveAspectRatio="xMidYMid meet">
        {/* Court floor (white) */}
        <rect x="0" y="0" width={W} height={H} fill={FLOOR}/>

        {/* Outer playing area boundary */}
        <rect x="6" y="6" width={W - 12} height={H - 12} fill="none" stroke={LINE} strokeWidth="0.3"/>

        {/* Center line */}
        <line x1={W / 2} y1="6" x2={W / 2} y2={H - 6} stroke={LINE} strokeWidth="0.3"/>

        {/* Center circle */}
        <circle cx={W / 2} cy={H / 2} r="3" fill="none" stroke={LINE} strokeWidth="0.3"/>
        <circle cx={W / 2} cy={H / 2} r="0.4" fill={LINE}/>

        {/* 6m goal areas (left & right) — solid arcs */}
        <path d={`M 6 ${H/2 - 7} L 13 ${H/2 - 7} A 7 7 0 0 1 13 ${H/2 + 7} L 6 ${H/2 + 7}`}
              fill="rgba(107,45,92,0.06)" stroke={LINE} strokeWidth="0.3"/>
        <path d={`M ${W-6} ${H/2 - 7} L ${W-13} ${H/2 - 7} A 7 7 0 0 0 ${W-13} ${H/2 + 7} L ${W-6} ${H/2 + 7}`}
              fill="rgba(107,45,92,0.06)" stroke={LINE} strokeWidth="0.3"/>

        {/* 9m dotted lines (free-throw line) */}
        <path d={`M 6 ${H/2 - 9.5} L 11 ${H/2 - 9.5} A 9.5 9.5 0 0 1 11 ${H/2 + 9.5} L 6 ${H/2 + 9.5}`}
              fill="none" stroke={LINE} strokeWidth="0.25" strokeDasharray="0.7,0.5"/>
        <path d={`M ${W-6} ${H/2 - 9.5} L ${W-11} ${H/2 - 9.5} A 9.5 9.5 0 0 0 ${W-11} ${H/2 + 9.5} L ${W-6} ${H/2 + 9.5}`}
              fill="none" stroke={LINE} strokeWidth="0.25" strokeDasharray="0.7,0.5"/>

        {/* 7m penalty marks */}
        <line x1="13" y1={H/2} x2="14" y2={H/2} stroke={LINE} strokeWidth="0.3"/>
        <line x1={W-13} y1={H/2} x2={W-14} y2={H/2} stroke={LINE} strokeWidth="0.3"/>

        {/* 4m goalkeeper lines */}
        <line x1="10" y1={H/2 - 0.7} x2="10" y2={H/2 + 0.7} stroke={LINE} strokeWidth="0.3"/>
        <line x1={W-10} y1={H/2 - 0.7} x2={W-10} y2={H/2 + 0.7} stroke={LINE} strokeWidth="0.3"/>

        {/* Goals */}
        <rect x="5.4" y={H/2 - 1.5} width="0.6" height="3" fill="#dc2626" stroke={LINE} strokeWidth="0.15"/>
        <rect x={W-6} y={H/2 - 1.5} width="0.6" height="3" fill="#2563eb" stroke={LINE} strokeWidth="0.15"/>

        {/* HOTSPOTS */}
        {zones.map((z) => {
          const x = (z.x / 100) * W;
          const y = (z.y / 100) * H;
          const w = (z.w / 100) * W;
          const h = (z.h / 100) * H;
          const isSelected = selected?.id === z.id;
          const zoneColor = z.available ? AVAILABLE_GREEN : RESERVED_RED;
          const letter = z.letter || "";
          const fontSize = z.shape === "circle" ? 2.2 : Math.min(w, h) * 0.45;

          if (z.shape === "circle") {
            const r = w / 2;
            return (
              <g key={z.id} className="cursor-pointer" onClick={() => onSelect(z)} data-testid={`zone-${z.id}`}>
                <circle cx={x} cy={y} r={r * 1.4}
                  fill={zoneColor} fillOpacity={isSelected ? 0.95 : 0.85}
                  stroke="white" strokeWidth="0.45" filter="drop-shadow(0 0 1px rgba(0,0,0,0.3))"/>
                {z.available && (
                  <circle cx={x} cy={y} r={r * 1.4}
                    fill="none" stroke={zoneColor} strokeWidth="0.5"
                    style={{ animation: `pulseRing 2s infinite ease-out`, transformOrigin: `${x}px ${y}px` }}/>
                )}
                <text x={x} y={y + fontSize * 0.35} textAnchor="middle"
                      fontSize={fontSize} fontWeight="900" fill="white"
                      style={{ fontFamily: "'Bebas Neue', Impact, sans-serif", letterSpacing: "0.05em" }}>
                  {letter}
                </text>
              </g>
            );
          }
          return (
            <g key={z.id} className="cursor-pointer" onClick={() => onSelect(z)} data-testid={`zone-${z.id}`}>
              <rect x={x - w/2} y={y - h/2} width={w} height={h}
                rx="0.4"
                fill={zoneColor} fillOpacity={isSelected ? 0.98 : 0.88}
                stroke={isSelected ? LINE : "white"} strokeWidth={isSelected ? "0.4" : "0.25"}
                filter="drop-shadow(0 0 1px rgba(0,0,0,0.3))"/>
              {z.available && (
                <rect x={x - w/2} y={y - h/2} width={w} height={h} rx="0.4"
                  fill="none" stroke={zoneColor} strokeWidth="0.5"
                  style={{ animation: `pulseRect 2.4s infinite ease-out`, transformOrigin: `${x}px ${y}px` }}/>
              )}
              <text x={x} y={y + fontSize * 0.35} textAnchor="middle"
                    fontSize={fontSize} fontWeight="900" fill="white"
                    style={{ fontFamily: "'Bebas Neue', Impact, sans-serif", letterSpacing: "0.05em" }}>
                {letter}
              </text>
            </g>
          );
        })}
        <style>{`
          @keyframes pulseRect { 0% { opacity: 0.7; transform: scale(1); } 100% { opacity: 0; transform: scale(1.25); } }
          @keyframes pulseRing { 0% { opacity: 0.7; transform: scale(1); } 100% { opacity: 0; transform: scale(2); } }
        `}</style>
      </svg>

      {/* Caption */}
      <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-slate-700 gap-2 flex-wrap">
        <span className="bg-white/90 border border-slate-200 backdrop-blur px-2 py-1 rounded shadow-sm">Halle Polyvalente · Configuration officielle</span>
        <div className="flex gap-2">
          <span className="bg-white/90 border border-slate-200 backdrop-blur px-2 py-1 rounded inline-flex items-center gap-1.5 shadow-sm">
            <span className="w-2.5 h-2.5 rounded-full animate-pulse" style={{ background: AVAILABLE_GREEN }}/> Disponible
          </span>
          <span className="bg-white/90 border border-slate-200 backdrop-blur px-2 py-1 rounded inline-flex items-center gap-1.5 shadow-sm">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: RESERVED_RED }}/> Réservé
          </span>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Page
// ============================================================
export default function SportPartenaires() {
  const { customer, login } = useCustomerAuth();
  const [zones, setZones] = useState([]);
  const [selected, setSelected] = useState(null);
  const [zoneModal, setZoneModal] = useState(null);
  const [partners, setPartners] = useState([]);
  const [showLogin, setShowLogin] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPwd, setLoginPwd] = useState("");
  const [loginError, setLoginError] = useState("");
  const [contactForm, setContactForm] = useState({
    company: "",
    name: "",
    email: "",
    phone: "",
    sector: "",
    budget: "",
    message: "Bonjour,\n\nJe représente une entreprise intéressée par un partenariat avec l'Istres Provence Handball pour la saison 2025-2026.\n\nNous recherchons une visibilité auprès de votre public passionné et nous aimerions étudier les opportunités qui correspondraient le mieux à notre stratégie de communication.\n\nPourriez-vous nous recontacter pour échanger sur :\n• Les offres disponibles\n• Les modalités et conditions\n• Un rendez-vous pour présenter votre projet\n\nMerci d'avance,",
  });
  const [contactSent, setContactSent] = useState(false);

  const handleZoneClick = (z) => {
    setSelected(z);
    setZoneModal(z);
  };

  useEffect(() => {
    api.get("/sport/court-zones").then((r) => setZones(r.data?.zones || [])).catch(() => {});
    api.get("/sport/partners").then((r) => setPartners(r.data?.partners || r.data || [])).catch(() => {});
  }, []);

  // Load directory when authenticated
  useEffect(() => {
    if (customer) {
      api.get("/sport/partner-directory-secure")
        .then((r) => { /* directory data already covered via partners list (with contact unmasked) */ })
        .catch(() => {});
    }
  }, [customer]);

  const handleContactSubmit = (e) => {
    e.preventDefault();
    setContactSent(true);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      await login(loginEmail, loginPwd);
      setShowLogin(false);
    } catch (err) {
      setLoginError(err?.response?.data?.detail || "Identifiants incorrects");
    }
  };

  return (
    <div style={{ background: BG_DARK }}>
      {/* HERO */}
      <section className="relative pt-32 pb-12 sm:pt-40 sm:pb-16 overflow-hidden" style={{ background: `linear-gradient(135deg, ${BG_DARK} 0%, ${PRIMARY} 100%)` }}>
        <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(circle at 80% 30%, rgba(245,166,35,0.4), transparent 60%)" }}/>
        <div className="relative max-w-6xl mx-auto px-4 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-pink-400 font-black tracking-[0.4em] text-xs uppercase mb-3">
            Espace Partenaires
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="text-white font-black uppercase italic" style={{ fontSize: "clamp(2.25rem, 6vw, 4.5rem)", lineHeight: 0.95 }}>
            Rejoignez le <span style={{ color: SECONDARY }}>Business Club</span> !
          </motion.h1>
          <p className="mt-4 text-white/80 max-w-2xl text-base">Plus de <strong className="text-pink-400">2 000 spectateurs</strong> par match, <strong className="text-pink-400">100 000+</strong> personnes touchées chaque saison via TV, presse et réseaux sociaux. Votre marque mérite la lumière de la StarLigue.</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href="#zones" data-testid="cta-explore-zones"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-black uppercase text-xs tracking-widest shadow-xl transition hover:scale-105"
              style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, color: BG_DARK }}>
              Explorer les zones <ArrowRight size={14}/>
            </a>
            <a href="#contact" data-testid="cta-become-partner"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-black uppercase text-xs tracking-widest border-2 border-white/30 text-white hover:bg-white hover:text-black transition">
              <Megaphone size={14}/> Devenir partenaire
            </a>
          </div>
        </div>
        <TornEdge position="bottom" color="#fbf7fa" variant="shred" height={56}/>
      </section>

      {/* MAIN 2-COL LAYOUT — Sidebar + (Video + Partners showcase + Court) */}
      <section id="zones" className="relative py-16 sm:py-20" style={{ background: "#fbf7fa" }}>
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-[300px,1fr] gap-8">
            {/* Sidebar */}
            <PartnersSidebar
              partnerCount={partners.length}
              customer={customer}
              onLogin={() => setShowLogin(true)}
              onScrollContact={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
            />

            {/* Main content stack */}
            <div className="space-y-8">
              {/* Club presentation video */}
              <div id="presentation">
                <ClubPresentation/>
              </div>

              {/* Business events gallery */}
              <BusinessEventsGallery/>

              {/* Partners showcase */}
              <PartnersShowcase partners={partners}/>

              {/* Court interactive */}
              <div className="rounded-3xl bg-white shadow-xl border border-slate-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100">
                  <div className="text-pink-600 font-black tracking-[0.3em] text-[10px] uppercase">Plan terrain interactif</div>
                  <h3 className="font-black uppercase italic text-xl sm:text-2xl mt-0.5" style={{ color: PRIMARY }}>
                    Choisissez votre <span style={{ color: SECONDARY }}>visibilité</span>
                  </h3>
                  <p className="text-slate-500 text-sm mt-2 max-w-2xl">Cliquez sur une zone du terrain pour découvrir l'emplacement, le format et les opportunités proposées.</p>
                </div>
                <div className="p-4 sm:p-6 grid lg:grid-cols-[1.6fr,1fr] gap-5">
                  <CourtPlan zones={zones} selected={selected} onSelect={handleZoneClick}/>
                  <div className="space-y-3">
                    <AnimatePresence mode="wait">
                      {selected ? (
                        <motion.div
                          key={selected.id}
                          initial={{ opacity: 0, y: 16 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -16 }}
                          className="rounded-2xl bg-white shadow-lg border border-slate-100 overflow-hidden"
                          data-testid="zone-detail"
                        >
                          <div className="px-5 py-4 text-white" style={{ background: TIER_INFO[selected.tier]?.bg || PRIMARY }}>
                            <div className="text-[10px] font-black uppercase tracking-widest opacity-90">{TIER_INFO[selected.tier]?.label}</div>
                            <h4 className="font-black text-lg uppercase italic leading-tight mt-1">{selected.name}</h4>
                          </div>
                          <div className="p-5 space-y-3">
                            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-slate-500">
                              <Shield size={12}/> {selected.dimensions}
                            </div>
                            <p className="text-slate-700 text-sm leading-relaxed">{selected.description}</p>
                            <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${selected.available ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${selected.available ? "bg-emerald-500 animate-pulse" : "bg-rose-500"}`}/>
                              {selected.available ? "Disponible" : "Réservé"}
                            </div>
                            <a href="#contact" data-testid="zone-cta-contact"
                              onClick={() => setContactForm((f) => ({ ...f, message: `Je m'intéresse à la zone "${selected.name}" (${selected.dimensions}).` }))}
                              className="mt-2 w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full font-black uppercase text-xs tracking-widest transition hover:scale-[1.02]"
                              style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, color: "white" }}>
                              Demander un devis <ArrowRight size={13}/>
                            </a>
                          </div>
                        </motion.div>
                      ) : (
                        <motion.div
                          key="empty"
                          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                          className="rounded-2xl border-2 border-dashed border-slate-200 p-8 text-center text-slate-500"
                        >
                          <Megaphone className="w-10 h-10 mx-auto mb-3 text-pink-500"/>
                          <h4 className="font-black uppercase text-sm" style={{ color: PRIMARY }}>Cliquez sur une zone</h4>
                          <p className="text-[12px] mt-2">Découvrez chaque emplacement publicitaire et son potentiel.</p>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <div className="rounded-2xl bg-slate-50 p-4 space-y-3 border border-slate-100">
                      <div>
                        <div className="text-pink-500 font-black tracking-widest text-[10px] uppercase mb-2">Disponibilité</div>
                        <ul className="space-y-1.5 text-[12px] text-slate-700">
                          <li className="flex items-center gap-2">
                            <span className="relative flex w-3 h-3">
                              <span className="absolute inset-0 rounded-full opacity-75 animate-ping" style={{ background: AVAILABLE_GREEN }}/>
                              <span className="relative w-3 h-3 rounded-full" style={{ background: AVAILABLE_GREEN }}/>
                            </span>
                            Zone disponible
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full" style={{ background: RESERVED_RED }}/>
                            Zone réservée
                          </li>
                        </ul>
                      </div>
                      <div className="border-t border-slate-200 pt-3">
                        <div className="text-pink-500 font-black tracking-widest text-[10px] uppercase mb-2">Niveaux partenaires</div>
                        <ul className="space-y-1.5 text-[12px] text-slate-700">
                          {Object.entries(TIER_INFO).map(([k, v]) => (
                            <li key={k} className="flex items-center gap-2">
                              <span className="w-3 h-3 rounded" style={{ background: v.bg }}/>
                              {v.label}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT FORM */}
      <section id="contact" className="relative py-20" style={{ background: BG_DEEP }}>
        <div className="max-w-4xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-pink-400 font-black tracking-[0.3em] text-xs uppercase mb-2">Devenir partenaire</div>
            <h2 className="text-white font-black text-3xl sm:text-4xl uppercase italic">
              Construisons <span style={{ color: SECONDARY }}>ensemble</span>
            </h2>
          </div>

          {contactSent ? (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              className="rounded-3xl bg-white p-10 text-center shadow-2xl">
              <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4"/>
              <h3 className="font-black text-2xl uppercase italic" style={{ color: PRIMARY }}>Demande envoyée !</h3>
              <p className="text-slate-600 mt-3">Notre équipe commerciale vous recontacte sous 48h.</p>
            </motion.div>
          ) : (
            <form onSubmit={handleContactSubmit} className="rounded-3xl bg-white shadow-2xl p-6 sm:p-10 space-y-4" data-testid="partner-contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="contact-company" placeholder="Entreprise *" required value={contactForm.company} onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })}
                  className="px-5 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-pink-500 focus:outline-none"/>
                <input data-testid="contact-name" placeholder="Votre nom *" required value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  className="px-5 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-pink-500 focus:outline-none"/>
                <input data-testid="contact-email" type="email" placeholder="Email *" required value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  className="px-5 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-pink-500 focus:outline-none"/>
                <input data-testid="contact-phone" placeholder="Téléphone" value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                  className="px-5 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-pink-500 focus:outline-none"/>
              </div>
              <textarea data-testid="contact-message" placeholder="Votre message — quelle zone vous intéresse ?" rows={4} value={contactForm.message} onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                className="w-full px-5 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-pink-500 focus:outline-none"/>
              <button data-testid="contact-submit" className="w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full font-black uppercase text-sm tracking-widest shadow-2xl hover:scale-[1.02] transition"
                style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, color: "white" }}>
                Envoyer ma demande <ArrowRight size={16}/>
              </button>
            </form>
          )}
        </div>
      </section>

      {/* PARTNER DIRECTORY — always visible, contacts masked unless logged in */}
      <section id="annuaire" className="relative py-20" style={{ background: BG_DARK }}>
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-pink-400 font-black tracking-[0.3em] text-xs uppercase mb-2 flex items-center justify-center gap-2">
              <Building2 size={11}/> Annuaire des partenaires
            </div>
            <h2 className="text-white font-black text-3xl sm:text-4xl uppercase italic">
              L'écosystème <span style={{ color: SECONDARY }}>du club</span>
            </h2>
            <p className="text-white/60 mt-3 max-w-2xl mx-auto text-sm">
              Découvrez toutes les entreprises partenaires de l'IPHB.
              {!customer && <> Les <strong className="text-pink-400">coordonnées (email & téléphone)</strong> sont accessibles aux partenaires connectés.</>}
            </p>
            {!customer && (
              <button onClick={() => setShowLogin(true)} data-testid="annuaire-login-btn"
                className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-black uppercase text-[11px] tracking-widest hover:scale-105 transition"
                style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, color: "white" }}>
                <Lock size={12}/> Se connecter pour voir les contacts
              </button>
            )}
          </div>

          {!partners.length ? (
            <div className="text-center text-white/50 italic py-12">Aucun partenaire dans l'annuaire pour le moment.</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="partner-directory">
              {partners.map((p, i) => (
                <motion.div key={p.id || i}
                  initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ delay: Math.min(i * 0.03, 0.4) }}
                  className="rounded-2xl bg-white/5 border border-white/10 p-5 hover:border-pink-500/40 transition relative overflow-hidden">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-full bg-white p-1.5 ring-2 ring-pink-500/30 shrink-0">
                      {p.logo_url ? <img src={p.logo_url} alt={p.name} className="w-full h-full object-contain"/> : <Star className="w-full h-full text-slate-300"/>}
                    </div>
                    <div className="min-w-0">
                      <div className="text-white font-black text-sm uppercase truncate">{p.name}</div>
                      <div className="text-pink-400 text-[10px] font-black tracking-widest uppercase">{TIER_INFO[p.tier]?.label || p.tier}</div>
                    </div>
                  </div>
                  {/* Sector / address (always visible) */}
                  {(p.sector || p.address) && (
                    <div className="space-y-1 text-[12px] mb-3">
                      {p.sector && <div className="text-white/60 italic">{p.sector}</div>}
                      {p.address && <div className="flex items-start gap-1.5 text-white/70"><MapPin size={11} className="mt-0.5 shrink-0"/> {p.address}</div>}
                    </div>
                  )}
                  {/* Description (always visible) */}
                  {p.description && (
                    <p className="text-white/70 text-[12px] leading-relaxed mb-3 line-clamp-3">{p.description}</p>
                  )}
                  {/* Contact section: locked unless logged in */}
                  <div className="border-t border-white/10 pt-3 space-y-1.5 text-[12px] relative">
                    {customer ? (
                      <>
                        {p.contact_name && <div className="text-white/85 font-bold">{p.contact_name}</div>}
                        {p.contact_email && <a href={`mailto:${p.contact_email}`} className="flex items-center gap-1.5 text-white/70 hover:text-pink-400"><Mail size={11}/> {p.contact_email}</a>}
                        {p.contact_phone && <a href={`tel:${p.contact_phone}`} className="flex items-center gap-1.5 text-white/70 hover:text-pink-400"><PhoneIcon size={11}/> {p.contact_phone}</a>}
                        {p.website_url && <a href={p.website_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-white/70 hover:text-pink-400"><Globe size={11}/> Site web</a>}
                      </>
                    ) : (
                      <div className="relative">
                        <div className="space-y-1.5 select-none blur-[5px] pointer-events-none" aria-hidden>
                          <div className="text-white/85 font-bold">Contact dirigeant</div>
                          <div className="flex items-center gap-1.5 text-white/70"><Mail size={11}/> contact@exemple.com</div>
                          <div className="flex items-center gap-1.5 text-white/70"><PhoneIcon size={11}/> 04 XX XX XX XX</div>
                        </div>
                        <button onClick={() => setShowLogin(true)} data-testid={`unlock-partner-${i}`}
                          className="absolute inset-0 flex items-center justify-center gap-2 text-pink-300 font-black uppercase tracking-widest text-[10px] hover:text-pink-200 transition">
                          <Lock size={12}/> Déverrouiller
                        </button>
                      </div>
                    )}
                    {/* Website always public */}
                    {!customer && p.website_url && (
                      <a href={p.website_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-white/60 hover:text-pink-400 mt-2"><Globe size={11}/> Site web public</a>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ZONE MEDIA MODAL — photos + video on click */}
      <AnimatePresence>
        {zoneModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setZoneModal(null)}
            className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md overflow-y-auto"
            data-testid="zone-media-modal">
            <div className="min-h-full flex items-start justify-center p-4 sm:p-8" onClick={(e) => e.stopPropagation()}>
              <motion.div initial={{ scale: 0.9, y: 30 }} animate={{ scale: 1, y: 0 }}
                className="w-full max-w-4xl rounded-3xl overflow-hidden shadow-2xl"
                style={{ background: `linear-gradient(135deg, ${BG_DEEP}, ${PRIMARY})` }}>
                <div className="px-6 py-4 flex items-center justify-between border-b border-white/10">
                  <div>
                    <div className="text-pink-400 text-[10px] font-black uppercase tracking-widest">{TIER_INFO[zoneModal.tier]?.label || "Zone publicitaire"}</div>
                    <h3 className="text-white font-black text-xl sm:text-2xl uppercase italic mt-1">{zoneModal.name}</h3>
                  </div>
                  <button data-testid="zone-modal-close" onClick={() => setZoneModal(null)} className="w-11 h-11 rounded-full bg-white/10 hover:bg-pink-500 hover:text-white text-white/80 transition flex items-center justify-center">
                    <X size={20}/>
                  </button>
                </div>
                <div className="p-6 grid md:grid-cols-2 gap-5">
                  <div className="space-y-3">
                    {(ZONE_MEDIA[zoneModal.id]?.video) && (
                      <div className="aspect-video rounded-2xl overflow-hidden border border-white/10">
                        <iframe title={zoneModal.name} src={ZONE_MEDIA[zoneModal.id].video}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen className="w-full h-full"/>
                      </div>
                    )}
                    <div className={`grid ${(ZONE_MEDIA[zoneModal.id]?.photos?.length || 0) > 1 ? "grid-cols-2" : "grid-cols-1"} gap-2`}>
                      {(ZONE_MEDIA[zoneModal.id]?.photos || ["/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F04%2F5F6A0557-scaled-e1776434253613.jpg"]).map((src, i) => (
                        <img key={i} src={src} alt={zoneModal.name} className="w-full aspect-video object-cover rounded-xl border border-white/10"/>
                      ))}
                    </div>
                  </div>
                  <div className="text-white">
                    <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest mb-3 ${zoneModal.available ? "bg-emerald-500 text-white" : "bg-rose-500 text-white"}`}>
                      <span className={`w-1.5 h-1.5 rounded-full bg-white ${zoneModal.available ? "animate-pulse" : ""}`}/>
                      {zoneModal.available ? "Disponible" : "Réservé"}
                    </div>
                    <div className="text-pink-300 text-[11px] font-black uppercase tracking-widest mb-1">Format</div>
                    <div className="text-white font-bold text-base mb-4">{zoneModal.dimensions}</div>
                    <p className="text-white/85 leading-relaxed mb-4">{zoneModal.description}</p>
                    {ZONE_MEDIA[zoneModal.id]?.benefits && (
                      <>
                        <div className="text-pink-300 text-[11px] font-black uppercase tracking-widest mb-2">Avantages clés</div>
                        <ul className="space-y-2 mb-5">
                          {ZONE_MEDIA[zoneModal.id].benefits.map((b, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-white/90">
                              <CheckCircle2 size={15} className="text-emerald-400 mt-0.5 shrink-0"/> {b}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                    <a href="#contact" data-testid="zone-modal-cta"
                      onClick={() => { setContactForm((f) => ({ ...f, message: `Je m'intéresse à la zone "${zoneModal.name}" (${zoneModal.dimensions}).` })); setZoneModal(null); }}
                      className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-black uppercase text-xs tracking-widest shadow-2xl hover:scale-105 transition w-full"
                      style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, color: "white" }}>
                      Demander un devis pour cette zone <ArrowRight size={14}/>
                    </a>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* BUSINESS SECTIONS — Tab-based pricing with brochure download */}
      <BusinessPricingTabs onCta={(name) => {
        setContactForm((f) => ({ ...f, message: `Je suis intéressé par l'offre : ${name}.\n\nMerci de me recontacter pour plus d'informations sur les modalités, les disponibilités et les conditions.` }));
        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
      }}/>

      {/* MOBILE STICKY CTA — Devenir partenaire (always visible on mobile) */}
      <div className="lg:hidden fixed bottom-4 inset-x-4 z-40" data-testid="mobile-cta-devenir-partenaire-wrap">
        <button
          onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
          className="w-full inline-flex items-center justify-center gap-3 px-5 py-4 rounded-full font-black uppercase text-sm tracking-widest text-white shadow-2xl active:scale-95 transition"
          style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}
          data-testid="mobile-cta-devenir-partenaire"
        >
          <Megaphone size={16}/> Devenir partenaire <ArrowRight size={14}/>
        </button>
      </div>

      {/* LOGIN MODAL */}
      <AnimatePresence>
        {showLogin && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowLogin(false)}>
            <motion.div initial={{ scale: 0.9, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden"
              data-testid="partner-login-modal">
              <div className="px-6 py-4 text-white flex items-center justify-between" style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${BG_DEEP})` }}>
                <div className="font-black uppercase tracking-widest text-sm flex items-center gap-2"><Lock size={14}/> Espace partenaire</div>
                <button onClick={() => setShowLogin(false)} className="text-white/70 hover:text-white"><X size={20}/></button>
              </div>
              <form onSubmit={handleLogin} className="p-6 space-y-4">
                <input data-testid="login-email" type="email" placeholder="Email" required value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-pink-500 focus:outline-none"/>
                <input data-testid="login-password" type="password" placeholder="Mot de passe" required value={loginPwd} onChange={(e) => setLoginPwd(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-pink-500 focus:outline-none"/>
                {loginError && <div className="text-rose-500 text-sm">{loginError}</div>}
                <button data-testid="login-submit" className="w-full px-6 py-3 rounded-full font-black uppercase text-xs tracking-widest shadow hover:scale-[1.02] transition"
                  style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, color: BG_DARK }}>
                  Se connecter
                </button>
                <div className="text-center text-[11px] text-slate-500">Pas encore partenaire ? <a href="#contact" className="text-pink-600 font-bold underline" onClick={() => setShowLogin(false)}>Demander un accès</a></div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================================
// BUSINESS SECTIONS — recreate offers from istreshandball.com/business/prestations/
// ============================================================
function BusinessSections({ setContactForm }) {
  const goContact = (subject) => {
    setContactForm((f) => ({ ...f, message: `Je suis intéressé par l'offre : ${subject}` }));
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      {/* HOSPITALITÉ VIP */}
      <BusinessBlock
        anchor="hospitalite"
        eyebrow="Hospitalité"
        title="Vivez le match côté <span style='color: #EC1F7C'>VIP</span>"
        intro="L'alliance parfaite entre business et passion. Recevez vos clients dans un cadre privilégié, échangez avec les joueurs pros et profitez d'un service traiteur premium."
        bg="white"
        offers={[
          {
            icon: Coffee, name: "Espace VIP",
            image: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F04%2F0J5A3051-scaled-e1775208468422.jpg",
            features: ["Bar et service traiteur", "Animations immersives", "Networking partenaires", "Rencontres joueurs pros"],
            cta: "Espace VIP",
          },
          {
            icon: Trophy, name: "Loge privée",
            image: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F04%2F0J5A3051-scaled-e1775208468422.jpg",
            features: ["Loge exclusive 12 personnes", "Hôtesse dédiée", "Bar continu", "Service traiteur premium"],
            cta: "Loge privée 12 pers.",
          },
          {
            icon: Sparkles, name: "Loge bord de terrain",
            image: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F02%2F0J5A1912-scaled-e1770806108734.jpg",
            features: ["Vue imprenable sur l'action", "Au plus près du jeu", "8e homme du match", "Expérience immersive"],
            cta: "Loge bord de terrain",
          },
        ]}
        onCta={goContact}
      />

      {/* AFFICHAGE MAILLOT */}
      <BusinessBlock
        anchor="affichage-maillot"
        eyebrow="Affichage maillot"
        title="Votre marque sur <span style='color: #EC1F7C'>nos couleurs</span>"
        intro="Diffusez votre image en BeIN Sports & Handball TV, devant des milliers de spectateurs sur l'ensemble des salles de France. Visibilité locale ET nationale toute la saison."
        bg="dark"
        offers={[
          {
            icon: Shirt, name: "Maillot de match",
            image: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F04%2F5F6A0557-scaled-e1776434253613.jpg",
            features: ["Diffusion BeIN Sports & Handball TV", "Présence nationale toute la saison", "Visibilité résumés & compilations", "Logo poitrine / dos / manche"],
            cta: "Maillot de match",
          },
          {
            icon: Zap, name: "Maillot d'échauffement",
            image: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F03%2F0J5A4559-scaled.jpg",
            features: ["Produit phare de la boutique", "Visible avant chaque match", "Caméras d'échauffement", "Aussi vendu aux supporters"],
            cta: "Maillot échauffement",
          },
          {
            icon: Briefcase, name: "Tenue staff & coachs",
            image: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F02%2F5F6A6737-scaled-e1771509757955.jpg",
            features: ["Tenue de match entraîneurs", "Veste d'échauffement", "T-shirt Serpilleros", "Maillot mascotte"],
            cta: "Tenue staff",
          },
        ]}
        onCta={goContact}
      />

      {/* VISIBILITÉ TV / ÉCRAN */}
      <BusinessBlock
        anchor="visibilite"
        eyebrow="Visibilité TV & Écran géant"
        title="Captez l'attention <span style='color: #EC1F7C'>caméra</span>"
        intro="Du LED bord de terrain à l'écran géant en passant par les stickers terrain, choisissez l'arme média qui maximisera votre exposition."
        bg="white"
        offers={[
          {
            icon: Tv, name: "Écran géant",
            image: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F04%2F5F6A0557-scaled-e1776434253613.jpg",
            features: ["Avant-match", "Mi-temps (30s)", "Temps morts (20s)", "Salle + télé simultanément"],
            cta: "Écran géant",
          },
          {
            icon: Volume2, name: "LED bord de terrain",
            image: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F03%2F0J5A7467-scaled-e1773324091159.jpg",
            features: ["80, 40 ou 20 mètres", "Animations dynamiques", "Passages 15 secondes", "Face caméra & tribune VIP"],
            cta: "LED bord de terrain",
          },
          {
            icon: Eye, name: "Sticker terrain",
            image: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F02%2F0J5A1912-scaled-e1770806108734.jpg",
            features: ["Format 450 × 130 cm", "+40 min d'expo TV / match", "Au plus près de l'action", "BeIN Sports & Handball TV"],
            cta: "Sticker terrain",
          },
          {
            icon: Trophy, name: "Parrainage de match",
            image: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F02%2F5F6A6737-scaled-e1771509757955.jpg",
            features: ["Coup d'envoi fictif sponsorisé", "24 passages LED autour du terrain", "Passages écran géant", "12 places Espace Premium"],
            cta: "Parrainage match",
          },
        ]}
        onCta={goContact}
      />

      {/* ÉVÉNEMENTIEL */}
      <BusinessBlock
        anchor="evenementiel"
        eyebrow="Événementiel & Cohésion"
        title="Vivez le hand <span style='color: #EC1F7C'>en équipe</span>"
        intro="Créez des moments uniques pour vos collaborateurs et vos clients : initiation handball, conférences, soirées partenaires avec les pros."
        bg="dark"
        offers={[
          {
            icon: Users, name: "Journée cohésion",
            image: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F02%2F5F6A6737-scaled-e1771509757955.jpg",
            features: ["Initiation handball par les pros", "Esprit d'équipe & motivation", "Adapté à votre demande", "Souvenir inoubliable"],
            cta: "Journée cohésion",
          },
          {
            icon: Briefcase, name: "Conférence sur-mesure",
            image: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F02%2F0J5A1912-scaled-e1770806108734.jpg",
            features: ["Mise à disposition d'un espace", "Service traiteur de qualité", "Intervention de joueurs pros", "Témoignage haut niveau"],
            cta: "Conférence",
          },
          {
            icon: Sparkles, name: "Soirée avec l'effectif",
            image: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F02%2F0J5A1912-scaled-e1770806108734.jpg",
            features: ["Afterwork partenaires", "Présence de l'effectif pro", "Networking exclusif", "Dans les locaux d'un partenaire"],
            cta: "Soirée afterwork",
          },
        ]}
        onCta={goContact}
      />

      {/* COMMUNICATION */}
      <BusinessBlock
        anchor="communication"
        eyebrow="Communication digitale & print"
        title="Multipliez vos <span style='color: #EC1F7C'>canaux</span>"
        intro="Affiches, e-billets, programme, newsletter, posts sponsorisés, parrainage de résumé vidéo… des supports stratégiques pour toucher tous les publics."
        bg="white"
        offers={[
          {
            icon: Megaphone, name: "Pack logo + post",
            image: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F03%2F0J5A9819-scaled-e1774454113383.jpg",
            features: ["Affiches de match", "E-billets", "Programme de match", "Post sponsorisé"],
            cta: "Pack communication",
          },
          {
            icon: Tv, name: "Parrainage résumé vidéo",
            image: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F03%2F0J5A0165-scaled.jpg",
            features: ["Encart programme de match", "Encart newsletter club", "Stories jour de match", "Réseaux sociaux club"],
            cta: "Parrainage résumé",
          },
          {
            icon: Sparkles, name: "Animation mi-temps",
            image: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F02%2F0J5A1912-scaled-e1770806108734.jpg",
            features: ["Naming animation mi-temps", "Clap Clap supporters", "Action en présence des joueurs", "Maillot mascotte"],
            cta: "Animation mi-temps",
          },
        ]}
        onCta={goContact}
      />
    </>
  );
}

function BusinessBlock({ anchor, eyebrow, title, intro, offers, bg = "white", onCta }) {
  const isDark = bg === "dark";
  const sectionBg = isDark ? BG_DEEP : "#fbf7fa";
  const titleColor = isDark ? "white" : PRIMARY;
  const introColor = isDark ? "rgba(255,255,255,0.75)" : "#64748b";

  return (
    <section id={anchor} className="relative py-20" style={{ background: sectionBg }}>
      <div className="max-w-6xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12 max-w-3xl mx-auto">
          <div className="text-pink-500 font-black tracking-[0.3em] text-xs uppercase mb-3">{eyebrow}</div>
          <h2 className="font-black text-3xl sm:text-4xl uppercase italic"
            style={{ color: titleColor }}
            dangerouslySetInnerHTML={{ __html: title }}/>
          <p className="mt-4 text-sm sm:text-base" style={{ color: introColor }}>{intro}</p>
        </div>

        <div className={`grid sm:grid-cols-2 ${offers.length > 3 ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-5`}>
          {offers.map((o, i) => (
            <motion.div key={o.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className="group rounded-3xl overflow-hidden shadow-2xl flex flex-col transition hover:-translate-y-1"
              style={{ background: isDark ? "rgba(255,255,255,0.04)" : "white", border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #f1eaf3" }}
              data-testid={`offer-${anchor}-${i}`}
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={o.image} alt={o.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition duration-700"/>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"/>
                <div className="absolute top-4 left-4 inline-flex items-center justify-center w-11 h-11 rounded-full text-white shadow-xl"
                  style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}>
                  <o.icon size={18}/>
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-white font-black text-lg sm:text-xl uppercase italic leading-tight">{o.name}</h3>
                </div>
              </div>
              <div className="flex-1 p-5 flex flex-col" style={{ color: isDark ? "white" : "#1a0820" }}>
                <ul className="space-y-2 flex-1">
                  {o.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2 text-[13px]" style={{ color: isDark ? "rgba(255,255,255,0.85)" : "#475569" }}>
                      <CheckCircle2 size={14} className="text-emerald-500 mt-0.5 shrink-0"/> {f}
                    </li>
                  ))}
                </ul>
                <button onClick={() => onCta(o.cta)}
                  data-testid={`offer-cta-${anchor}-${i}`}
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-black uppercase text-[11px] tracking-widest hover:scale-[1.02] transition"
                  style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, color: "white" }}>
                  Demander un devis <ArrowRight size={12}/>
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
